/** Customer context (M29): inbox card, contacts and the customer 360 view. Shell created by WP-M29-SCHEMA1: later work packages add keys inside their group (en and fil together, no dots inside key segments). */
export const customerContext = {
  card: {
    title: "Customer",
    verifiedBy: "Verified by operator · {{time}}",
    setByStaff: "Set by staff · {{time}}",
    staleAt: "Stale · last update {{time}}",
    staleHint: "Your system has not updated this customer in over a day. Check before you rely on it.",
    noContext: "Nothing from your system yet",
    reference: "Reference",
    tier: "Tier",
    status: "Status",
    notSet: "Not set",
    asOf: "As of",
    personal: "Personal data",
    live: "Updates by itself while this chat is open.",
    loading: "Loading the customer",
    errorTitle: "The customer details did not load",
    errorBody: "Your chat is not affected. Try again in a moment.",
    retry: "Try again",
    statuses: {
      active: "Active",
      restricted: "Restricted",
      suspended: "Suspended",
      self_excluded: "Self-excluded",
      closed: "Closed",
      kyc_pending: "Identity check pending",
    },
  },
  fields: {
    title: "Fields from your system",
    empty: "No fields from your system yet.",
    sourceAt: "{{source}} · {{time}}",
    yes: "Yes",
    no: "No",
    source: {
      launch: "Secure launch",
      context_api: "Context API",
      signed_event: "Signed event",
      connector_sync: "Connector sync",
      console: "Staff",
    },
    columns: {
      reference: "Reference",
      tier: "Tier",
      status: "Status",
      source: "Source",
    },
    noValue: "No value",
    stale: "Stale",
    sourced: "From your system",
  },
  reveal: {
    button: "Reveal",
    hide: "Hide",
    buttonLabel: "Reveal the customer reference",
    hideLabel: "Hide the customer reference",
    revealed: "Full reference",
    needsPermission: "The full reference needs a permission. Ask an owner or admin.",
    failed: "The reference could not be shown. Try again.",
    audited: "Each reveal is written to the audit log.",
  },
  lookups: {
    title: "Lookups in this chat",
    bound: "Bound to this customer",
    empty: "No lookups in this chat",
    emptyHint: "When the assistant checks your system for this customer, each check is listed here.",
    errorTitle: "The lookups did not load",
    retry: "Try again",
    via: "via {{connector}}",
    outcome: {
      ok: "Answered",
      stale: "Saved value used",
      failed: "Failed",
    },
    operations: {
      get_customer_support_profile: "Customer support profile",
    },
  },
  banner: {
    title: "Kept current from your system",
    body: "These values come from {{source}} and update by themselves. Last update {{time}}.",
    bodyNoTime: "These values come from {{source}} and update by themselves.",
    staleBody: "The last update is over a day old.",
    drawerTitle: "Customer fields",
    drawerEmpty: "Your system has not sent any fields for this customer yet.",
    errorTitle: "The customer fields did not load",
    retry: "Try again",
    loading: "Loading the customer fields",
  },
  timeline: {
    title: "Timeline",
    subtitle: "Newest first.",
    filterLabel: "Show on the timeline",
    showAll: "Show everything",
    kinds: { chat: "Chats", ticket: "Tickets", context: "Status", rating: "Ratings" },
    entry: {
      chat: "Chat",
      chatUntitled: "Chat with the customer",
      ticket: "Ticket {{number}}",
      context: "Status changed",
      contextCreated: "Customer record created",
      ratingChat: "Rated a chat",
      ratingTicket: "Rated ticket {{number}}",
      pushedBy: "Sent by {{source}}",
    },
    chatStatus: { open: "Open", waiting: "Waiting", resolved: "Resolved", closed: "Closed", abandoned: "Left the chat" },
    ticketStatus: { new: "New", open: "Open", pending: "Waiting for the client", on_hold: "On hold", resolved: "Resolved", closed: "Closed" },
    handledBy: { ai: "Handled by the assistant", staff: "Handled by a teammate" },
    score: "{{score}} out of {{scale}}",
    change: {
      tier: "Tier",
      accountStatus: "Status",
      displayName: "Name",
      locale: "Language",
      fromTo: "{{label}}: {{from}} → {{to}}",
      set: "{{label}} set to {{to}}",
      cleared: "{{label}} cleared (was {{from}})",
      changed: "{{label}} changed",
      hidden: "Personal data, not shown",
    },
    openChat: "Open the chat",
    openTicket: "Open the ticket",
    loadMore: "Show older",
    end: "That is everything so far.",
    loading: "Loading the timeline",
    empty: {
      title: "Nothing on the timeline yet",
      body: "Chats, tickets, status changes and ratings for this customer appear here as they happen.",
      cta: "Open the inbox",
      filteredTitle: "Nothing matches these filters",
      filteredBody: "Show everything to see the whole timeline again.",
    },
    error: {
      title: "The timeline did not load",
      body: "Nothing was changed. Try again in a moment.",
      retry: "Try again",
    },
  },
  /** Settings > Attributes: the "AI can see this" switch (PDI-007, WP-M29-AI-CONTEXT). */
  aiVisible: {
    label: "AI can see this",
    hint: "When on, the assistant can read this value in one short line about the customer. Customer reference numbers never reach the assistant.",
    column: "AI",
    on: "AI can see",
    off: "Not shared",
    notApplicable: "Not applicable",
    personal: "This holds personal data, so it stays off unless you turn it on. Phone numbers, e-mails and similar values are masked before the assistant sees them.",
    hidden: "This field is hidden from the assistant, so it cannot be turned on.",
    visitorOnly: "Only customer (visitor) attributes can be shown to the assistant.",
  },
} as const;
